import React, { useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { pdfjs } from 'react-pdf'

import { Fetch } from '@/utils/Fetch'
import { DocumentType } from '@/components/features/Document/types'

import { FilesUploadPropsType } from '../types'

type PageSize = {
  width: number
  height: number
}

type LocationSummary = {
  label: string
  page: number
}

type ReplaceResult = {
  removedLocations: LocationSummary[]
  movedLocations: LocationSummary[]
}

const readPageSizes = async (file: File): Promise<PageSize[]> => {
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer(), isEvalSupported: false }).promise
  const pageSizes: PageSize[] = []
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    pageSizes.push({ width: page.view[2] - page.view[0], height: page.view[3] - page.view[1] })
  }
  return pageSizes
}

const formatLocations = (locations: LocationSummary[]) =>
  locations.map((location) => `${location.label}（${location.page} ページ）`).join('、')

export const useDraftDocumentReplacer = (
  contractSetting: FilesUploadPropsType['contractSetting'],
  isFormDirty: boolean,
  refreshContractSetting?: () => void,
) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [targetDocument, setTargetDocument] = useState<DocumentType | null>(null)
  const [isBlocked, setIsBlocked] = useState(false)
  const [replacement, setReplacement] = useState<{ file: File; pageSizes: PageSize[] } | null>(null)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [isReplacing, setIsReplacing] = useState(false)
  const [result, setResult] = useState<ReplaceResult | null>(null)
  const unsentContractsCount = contractSetting?.draftContractsCount || 0

  const removedLocations: LocationSummary[] =
    replacement && replacement.pageSizes.length > 0 && targetDocument
      ? (contractSetting?.questionItems || []).flatMap((item) =>
          (item.outputLocations || [])
            .filter(
              (location) => location.documentId === targetDocument.id && location.page > replacement.pageSizes.length,
            )
            .map((location) => ({ label: item.label, page: location.page })),
        )
      : []

  const requestReplaceDocument = (document: DocumentType) => {
    setResult(null)
    if (unsentContractsCount > 0) {
      setIsBlocked(true)
      return
    }
    setTargetDocument(document)
    fileInputRef.current?.click()
  }

  const selectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !targetDocument) return
    try {
      setErrorMessages([])
      setReplacement({ file, pageSizes: await readPageSizes(file) })
    } catch {
      setErrorMessages(['PDF を読み込めませんでした。別のファイルを選んでください。'])
      setReplacement({ file, pageSizes: [] })
    }
  }

  const closeReplacement = () => {
    setReplacement(null)
    setTargetDocument(null)
    setErrorMessages([])
  }

  const submitReplacement = async () => {
    if (!contractSetting || !targetDocument || !replacement) return
    setIsReplacing(true)
    const body = new FormData()
    body.append('draft_document', replacement.file)
    body.append('page_sizes', JSON.stringify(replacement.pageSizes))
    try {
      const res = await Fetch(
        `/api/v1/contract_settings/${contractSetting.id}/draft_documents/${targetDocument.id}`,
        'PATCH',
        body,
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrorMessages(json.errors || ['差し替えに失敗しました。'])
        return
      }
      setResult(json)
      closeReplacement()
      refreshContractSetting && refreshContractSetting()
    } catch {
      setErrorMessages(['差し替えに失敗しました。'])
    } finally {
      setIsReplacing(false)
    }
  }

  const replacerElement = (
    <>
      <input ref={fileInputRef} type="file" accept="application/pdf" className="d-none" onChange={selectFile} />
      {result && (
        <Alert
          variant={result.movedLocations.length > 0 || result.removedLocations.length > 0 ? 'warning' : 'success'}
          dismissible
          onClose={() => setResult(null)}
          className="mt-3 mb-0"
        >
          <p className="mb-0">PDF を差し替えました。</p>
          {result.movedLocations.length > 0 && (
            <p className="mb-0 mt-1">
              ページからはみ出していた配置 {result.movedLocations.length}{' '}
              件をページ内に移しました。位置を確認してください: {formatLocations(result.movedLocations)}
            </p>
          )}
          {result.removedLocations.length > 0 && (
            <p className="mb-0 mt-1">
              新しい PDF にないページの配置 {result.removedLocations.length} 件を削除しました:{' '}
              {formatLocations(result.removedLocations)}
            </p>
          )}
        </Alert>
      )}
      <Modal show={isBlocked} onHide={() => setIsBlocked(false)}>
        <Modal.Header closeButton>差し替えできません</Modal.Header>
        <Modal.Body>
          <p>
            このテンプレートで作った未送付の契約書が{unsentContractsCount}件あるため、このテンプレートの PDF
            は差し替えられません。
          </p>
          <p className="mb-0">差し替える場合は、テンプレートをコピーし、コピーしたテンプレートで行ってください。</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsBlocked(false)}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!replacement} onHide={closeReplacement}>
        <Modal.Header closeButton>書類を差し替え</Modal.Header>
        <Modal.Body>
          <p>
            「{targetDocument?.name}」を「{replacement?.file.name}」に差し替えます。配置は残ります。
          </p>
          {removedLocations.length > 0 && (
            <p className="text-danger">
              新しい PDF にないページの配置 {removedLocations.length} 件は削除されます:{' '}
              {formatLocations(removedLocations)}
            </p>
          )}
          <p className="mb-0">ページからはみ出す配置は、ページ内の端に移します。</p>
          {isFormDirty && <p className="mb-0 mt-2">保存していない変更は、差し替えると消えます。</p>}
          {errorMessages.map((message) => (
            <p key={message} className="text-danger mb-0 mt-2" role="alert">
              {message}
            </p>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeReplacement} disabled={isReplacing}>
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={submitReplacement}
            disabled={isReplacing || !replacement || replacement.pageSizes.length === 0}
          >
            {isReplacing ? '差し替え中' : '差し替える'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )

  return { requestReplaceDocument, replacerElement }
}
